import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import SVG from "@/assets/svg";
import { TCheckInCheckOut } from "@/component/drawer/DetailTab";
import GoogleMap from "@/component/map";
import { CompactServiceUser } from "@/interfaces/IServiceUser";
import { useAppSelector } from "@/redux/hooks";
import { $term, getHaversineDistanceInMeters } from "@/utils/helpers";
import { DialogueStyled } from "../dialogueStyled";
import { LocationMismatchStyled } from "./styled";

type LocationMismatchProps = {
  onClose: () => void;
  selectedLog?:
    | (TCheckInCheckOut & {
        type: "checkIn" | "checkOut";
      })
    | null;
  serviceUser?: CompactServiceUser;
};

export default function LocationMismatch({
  selectedLog,
  serviceUser,
  onClose,
}: LocationMismatchProps) {
  const { terms } = useAppSelector((state) => state.terms);

  const getPatientLocation = () => {
    if (selectedLog?.type === "checkIn") {
      if (
        selectedLog?.checkInInfo?.clientLocation?.lat &&
        selectedLog?.checkInInfo?.clientLocation?.lng
      ) {
        return {
          lat: selectedLog.checkInInfo.clientLocation.lat,
          lng: selectedLog.checkInInfo.clientLocation.lng,
        };
      } else {
        return serviceUser?.addressCoordinates;
      }
    } else {
      if (
        selectedLog?.checkOutInfo?.clientLocation?.lat &&
        selectedLog?.checkOutInfo?.clientLocation?.lng
      ) {
        return {
          lat: selectedLog.checkOutInfo.clientLocation.lat,
          lng: selectedLog.checkOutInfo.clientLocation.lng,
        };
      } else {
        return serviceUser?.addressCoordinates;
      }
    }
  };

  const getPatientAddress = () => {
    if (selectedLog?.type === "checkIn") {
      return (
        selectedLog?.checkInInfo?.clientAddress || serviceUser?.postCode || ""
      );
    } else {
      return (
        selectedLog?.checkOutInfo?.clientAddress || serviceUser?.postCode || ""
      );
    }
  };

  const getCarerMarkerCoords = () => {
    if (selectedLog?.type === "checkIn") {
      if (
        selectedLog?.checkInInfo?.coord?.lat &&
        selectedLog?.checkInInfo?.coord?.lng
      ) {
        return {
          lat: selectedLog.checkInInfo.coord.lat,
          lng: selectedLog.checkInInfo.coord.lng,
        };
      } else {
        return null;
      }
    } else {
      if (
        selectedLog?.checkOutInfo?.coord?.lat &&
        selectedLog?.checkOutInfo?.coord?.lng
      ) {
        return {
          lat: selectedLog.checkOutInfo.coord.lat,
          lng: selectedLog.checkOutInfo.coord.lng,
        };
      } else {
        return null;
      }
    }
  };

  const distanceFromPatient = getHaversineDistanceInMeters({
    lat1: getCarerMarkerCoords()?.lat,
    lon1: getCarerMarkerCoords()?.lng,
    lat2: getPatientLocation()?.lat,
    lon2: getPatientLocation()?.lng,
  });

  const type = selectedLog?.type === "checkIn" ? "in" : "out";

  const distanceInKm = distanceFromPatient
    ? (distanceFromPatient / 1000).toFixed(2)
    : "N/A";

  return (
    <LocationMismatchStyled>
      <DialogueStyled>
        <Box className="createEvent">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* <Typography variant="h3" sx={{ fontWeight: 600, fontSize: "20px" }}>
            Are you sure you want to cancel visit?
          </Typography> */}

            <Typography variant="h3" sx={{ fontWeight: 600, fontSize: "20px" }}>
              {"Location Mismatch Detected"}
            </Typography>

            <Box onClick={onClose} sx={{ cursor: "pointer" }}>
              <SVG.Cross />
            </Box>
          </Box>

          <Box className="Detail">
            {distanceFromPatient ? (
              <span className="timer">
                {selectedLog?.carer?.name} checked {type} approximately{" "}
                {distanceInKm} kilometres away from secure check-
                {type} zone
              </span>
            ) : (
              <span className="timer">No location detected</span>
            )}
            <p className="detail">
              {$term(terms?.data?.serviceUser || "Client")} location:{" "}
              {getPatientAddress() || ""}
            </p>
            <Box className="secure-data-layout">
              <SVG.Location width="15px" />
              <Typography className="secure">
                Secure check-{type} zone:{" "}
                <span className="detail">
                  {serviceUser?.checkInRadius} metres
                </span>{" "}
              </Typography>
            </Box>
            <Box className="secure-data-layout">
              <SVG.Users width="15px" />
              <Typography className="secure">
                {$term(terms?.data?.staff || "Carer")} distance from{" "}
                {$term(terms?.data?.serviceUser || "client")} home:{" "}
                <span className="detail">
                  {distanceFromPatient
                    ? `Approx. ${distanceInKm} kilometres`
                    : "No location provided by the carer"}
                </span>{" "}
              </Typography>
            </Box>
            <Box sx={{ marginTop: "10px", borderRadius: "20px" }}>
              <GoogleMap
                center={getPatientLocation()}
                // setCenter={setCenter}
                containerStyle={{
                  width: "100%",
                  margin: "0 auto",
                  height: "450px",
                  borderRadius: "20px",
                  border: "0",
                }}
                disabled
                radius={serviceUser?.checkInRadius}
                additonalMarker={getCarerMarkerCoords()}
              />
            </Box>
          </Box>
        </Box>
      </DialogueStyled>
    </LocationMismatchStyled>
  );
}
